import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Sparkles,
  Boxes,
  Tag,
  Truck,
  Clock,
  MapPin,
  Wine,
  Layers,
  Shield,
  Home,
  ListChecks,
  Scale,
  Tags,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Livraison de cartons de déménagement {city.aCity}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce que charger des cartons volumineux dans une voiture
              personnelle est une contrainte évitable, notre service de
              livraison s&apos;invite chez vous avec des règles
              strictes.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Une dépose sur-mesure",
                body: "Nous livrons tout votre matériel à plat directement devant votre porte à La Celle-Saint-Cloud, mais aussi dans les villes voisines comme Bougival, Rueil-Malmaison, Le Chesnay-Rocquencourt et Louveciennes.",
                Icon: MapPin,
              },
              {
                title: "La garantie d'un matériel sain",
                body: "Vos cartons vous sont remis dans un état de propreté impeccable, totalement secs et prêts à être pliés pour commencer vos pièces immédiatement.",
                Icon: ShieldCheck,
              },
              {
                title: "Le respect de votre emploi du temps",
                body: "Pas de journée bloquée à attendre ; nous fixons ensemble un créneau horaire précis pour coller parfaitement à votre organisation.",
                Icon: Clock,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Cartons spéciaux vaisselle ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Spécial vaisselle
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Cartons spéciaux pour la vaisselle : sécurisez vos objets
            fragiles
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            L&apos;emballage de la cuisine est souvent la source de
            stress principale lors des préparatifs. Nos solutions
            spécifiques éliminent les risques de casse.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Croisillons */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Wine className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Les cartons verres et assiettes avec croisillons
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Plus besoin d&apos;envelopper chaque élément dans des
              tonnes de journaux. Nos cartons intègrent des alvéoles
              en carton rigide qui séparent et immobilisent chaque
              pièce de vaisselle fine. Vos verres à pied et vos
              assiettes voyagent ainsi isolés des chocs extérieurs.
            </p>
            <div className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Alvéoles en carton rigide
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Chaque pièce immobilisée
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Idéal verres à pied &amp; assiettes
              </div>
            </div>
          </div>

          {/* Manutention sécurisée */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Layers className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Une manutention sécurisée
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Grâce à une double cannelure renforcée, ces cartons
              spécifiques ne se déforment pas, même lorsqu&apos;ils
              sont empilés sous de lourdes charges. C&apos;est la
              garantie de retrouver votre service de table intact dès
              la première ouverture dans votre nouvelle cuisine
              {" "}{city.aCity}.
            </p>
            <div className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Double cannelure renforcée
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Empilable sous charges lourdes
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Service de table intact
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-la-celle-saint-cloud-cta-shop"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir nos cartons vaisselle
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/kits-cartons"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-la-celle-saint-cloud-cta-kits"
          >
            <Tag className="h-4 w-4" strokeWidth={2.6} />
            Découvrir les kits
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Méthode du tri (4 steps) ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Méthode du tri
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            La méthode du tri efficace avant la mise en boîte
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Profitez de l&apos;assemblage de vos cartons pour alléger
            votre volume et optimiser l&apos;espace le jour J grâce à
            ces étapes simples.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {[
            {
              step: "1",
              title: "La règle des pièces",
              body: "Emballez une pièce à la fois (commencez par les objets non quotidiens comme la bibliothèque ou la cave) pour ne pas vous laisser déborder.",
              Icon: Home,
            },
            {
              step: "2",
              title: "Le système des trois piles",
              body: "Classez ce qui ne va pas dans vos futurs cartons en trois catégories nettes : à donner, à vendre, ou à jeter.",
              Icon: ListChecks,
            },
            {
              step: "3",
              title: "Le pesage intelligent",
              body: "Utilisez nos cartons d'un volume adapté aux objets (les charges lourdes dans les petits formats) pour préserver votre dos et la structure du carton.",
              Icon: Scale,
            },
            {
              step: "4",
              title: "Le bouclage par carton",
              body: "Ne laissez jamais un carton ouvert en fin de journée ; scotchez-le et étiquetez-le immédiatement pour mesurer visuellement votre avancement.",
              Icon: Tags,
            },
          ].map(({ step, title, body, Icon }) => (
            <div
              key={step}
              className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6 flex flex-col"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#237cb5]">
                  Étape {step}
                </span>
              </div>
              <h3 className="mt-3 font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                {title}
              </h3>
              <p className="mt-2.5 text-[13.5px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-la-celle-saint-cloud-cta-accessoires"
          >
            <Tag className="h-4 w-4" strokeWidth={2.6} />
            Adhésifs &amp; étiquettes
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/livraison"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-la-celle-saint-cloud-cta-livraison"
          >
            <Truck className="h-4 w-4" strokeWidth={2.6} />
            Nos modalités de livraison
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-la-celle-saint-cloud-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
